import { apiFetch } from '@/lib/api'

export type ResourceFieldType =
  | 'AutoField'
  | 'BigAutoField'
  | 'BooleanField'
  | 'CharField'
  | 'DateField'
  | 'DateTimeField'
  | 'DecimalField'
  | 'EmailField'
  | 'FileField'
  | 'FloatField'
  | 'ForeignKey'
  | 'IntegerField'
  | 'JSONField'
  | 'ManyToManyField'
  | 'OneToOneField'
  | 'PositiveIntegerField'
  | 'PositiveSmallIntegerField'
  | 'SlugField'
  | 'SmallIntegerField'
  | 'TextField'
  | 'URLField'
  | 'UUIDField'
  | (string & {})

export type ResourceWidget =
  | 'text'
  | 'textarea'
  | 'number'
  | 'email'
  | 'url'
  | 'switch'
  | 'date'
  | 'datetime'
  | 'fk'
  | 'fk-multi'
  | 'json'
  | 'file'
  | 'readonly'
  | (string & {})

export interface ResourceChoice {
  value: string | number | boolean | null
  label: string
}

export interface ResourceField {
  name: string
  type: ResourceFieldType
  widget: ResourceWidget
  label: string
  hint: string
  required: boolean
  readonly: boolean
  redact: boolean
  choices?: ResourceChoice[]
  fk_app?: string
  fk_model?: string
  fk_resource?: string | null
  fk_label_field?: string
}

export interface ResourceExtraAction {
  id: string
  label: string
  method: string
  icon: string
  destructive: boolean
  bulk: boolean
  description: string
  requires_confirmation: boolean
}

export interface ResourceSchema {
  key: string
  app_label: string
  model_name: string
  label: string
  singular: string
  description: string
  icon: string
  group: string
  soft_delete: boolean
  list_fields: string[]
  search_fields: string[]
  filter_fields: string[]
  ordering: string[]
  form_fields: ResourceField[]
  detail_fields: ResourceField[]
  redact_fields: string[]
  extra_actions: ResourceExtraAction[]
}

export interface RegistryEntry {
  key: string
  label: string
  singular: string
  icon: string
  description: string
  app_label: string
  model_name: string
}

export interface RegistryResponse {
  groups: Record<string, RegistryEntry[]>
}

export interface CursorPage<T> {
  results: T[]
  next: string | null
  previous: string | null
}

export interface LookupItem {
  id: string | number
  label: string
}

export type ResourceRow = Record<string, unknown>

export function fetchRegistry(): Promise<RegistryResponse> {
  return apiFetch<RegistryResponse>('/api/admin/r/registry/')
}

export function fetchSchema(key: string): Promise<ResourceSchema> {
  return apiFetch<ResourceSchema>(
    `/api/admin/r/${encodeURIComponent(key)}/schema/`
  )
}

export function fetchList(
  key: string,
  params: Record<string, string | number | undefined>
): Promise<CursorPage<ResourceRow>> {
  const query = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue
    query.set(k, String(v))
  }
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return apiFetch<CursorPage<ResourceRow>>(
    `/api/admin/r/${encodeURIComponent(key)}/${suffix}`
  )
}

export function fetchDetail(
  key: string,
  id: string | number
): Promise<ResourceRow> {
  return apiFetch<ResourceRow>(
    `/api/admin/r/${encodeURIComponent(key)}/${encodeURIComponent(String(id))}/`
  )
}

export function createRow(
  key: string,
  body: Record<string, unknown>
): Promise<ResourceRow> {
  return apiFetch<ResourceRow>(`/api/admin/r/${encodeURIComponent(key)}/`, {
    method: 'POST',
    body
  })
}

export function updateRow(
  key: string,
  id: string | number,
  body: Record<string, unknown>
): Promise<ResourceRow> {
  return apiFetch<ResourceRow>(
    `/api/admin/r/${encodeURIComponent(key)}/${encodeURIComponent(String(id))}/`,
    {
      method: 'PATCH',
      body
    }
  )
}

export function deleteRow(key: string, id: string | number): Promise<void> {
  return apiFetch<void>(
    `/api/admin/r/${encodeURIComponent(key)}/${encodeURIComponent(String(id))}/`,
    {
      method: 'DELETE'
    }
  )
}

export function bulkDelete(
  key: string,
  ids: Array<string | number>
): Promise<{ deleted: number }> {
  return apiFetch<{ deleted: number }>(
    `/api/admin/r/${encodeURIComponent(key)}/bulk-delete/`,
    {
      method: 'POST',
      body: { ids }
    }
  )
}

export function lookup(
  key: string,
  q: string
): Promise<{ results: LookupItem[] }> {
  const query = q ? `?q=${encodeURIComponent(q)}` : ''
  return apiFetch<{ results: LookupItem[] }>(
    `/api/admin/r/${encodeURIComponent(key)}/lookup/${query}`
  )
}

export function exportCsvUrl(
  key: string,
  params: Record<string, string | undefined> = {}
): string {
  const query = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue
    query.set(k, v)
  }
  const suffix = query.toString() ? `?${query.toString()}` : ''
  const base =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
    'http://localhost:8000'
  return `${base}/api/admin/r/${encodeURIComponent(key)}/export/${suffix}`
}
